import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { TContentCardProps } from "@/types/base"
import { cva } from "class-variance-authority"
import { cn, formatDate } from "@/lib/utils"
import { Typography } from "@/components/ui/typography"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { INLINES } from "@contentful/rich-text-types"

const variants = cva("", {
  variants: {
    variant: {
      clip: "space-y-1 gap-4",
      card: "space-y-4",
      headline: "space-y-4",
      modal: "space-y-4",
    },
  },
  defaultVariants: {
    variant: "clip",
  },
})

const titleVariants = cva("font-semibold text-xl leading-tight", {
  variants: {
    variant: {
      clip: "text-2xl font-medium break-words text-foreground",
      card: "line-clamp-4",
      headline: "md:font-normal md:text-4xl text-center",
      modal: "font-normal text-4xl",
    },
    defaultVariants: {
      variant: "card",
    },
  },
})

const contentTypographyVariants = cva("text-xs font-medium leading-none", {
  variants: {
    variant: {
      slug: "text-muted-foreground dark:text-secondary-inverse",
      date: "text-muted-foreground dark:text-secondary-inverse",
    },
  },
})

const options = (type: "primary" | "secondary") => ({
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      const url = node.data.uri
      return (
        <a
          href={url}
          className={`underline ${type === "primary" ? "hover:text-foreground" : "hover:text-foreground"} transition-colors`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )
    },
  },
})

function Content({
  slug,
  headline,
  copy,
  videoUrl,
  externalLink,
  articleText,
  updatedAt,
  variant = "clip",
  type = "primary",
  subHeadline,
  feature = false,
}: TContentCardProps & {
  variant?: "headline" | "clip" | "card" | "modal"
  type?: "primary" | "secondary"
  // subHeadline?: string
  feature?: boolean
}) {
  // TODO - there should never be an externalLink here
  const hasButton = articleText || externalLink || videoUrl
  const buttonText = videoUrl && !articleText ? "Watch" : "Read"

  return (
    <article
      className={cn(variants({ variant }), {
        " text-center": feature === true,
      })}
    >
      <div className="flex flex-col gap-1.5">
        <div
          className={cn("flex items-center ", {
            "text-muted-foreground  ": type === "primary",
            "text-muted-inverse dark:text-secondary-inverse":
              type === "secondary",
            "justify-start": feature === false,
            "justify-center": feature === true,
          })}
        >
          {slug && (
            <>
              <Typography
                variant="body"
                className={contentTypographyVariants({ variant: "slug" })}
              >
                {slug}
              </Typography>
              <div className="ml-1 mr-1 ">|</div>
            </>
          )}
          <Typography
            variant="body"
            className={contentTypographyVariants({ variant: "date" })}
          >
            {formatDate(updatedAt)}
          </Typography>
        </div>
        <Typography variant="h2" className={cn(titleVariants({ variant }))}>
          {typeof headline === "string"
            ? headline
            : documentToReactComponents(headline, options(type))}
        </Typography>
        {copy && (
          <Typography
            variant="body"
            className={cn("flex items-center", {
              "text-muted-foreground": type === "primary",
              "text-muted-foreground dark:text-secondary-inverse":
                type === "secondary",
            })}
            as="div"
          >
            {documentToReactComponents(copy, options(type))}
          </Typography>
        )}
        {subHeadline && (
          <Typography
            variant="body"
            className={cn("flex items-center text-center", {
              "text-muted-foreground": type === "primary",
              "text-muted-inverse": type === "secondary",
            })}
            as="div"
          >
            {documentToReactComponents(subHeadline, options(type))}
          </Typography>
        )}
      </div>

      {hasButton && (
        <Button
          variant="link"
          className="text-muted-foreground group-hover:text-foreground"
        >
          {buttonText} <ArrowRight />
        </Button>
      )}
    </article>
  )
}

export default Content
